// /stacks/traffic/dev/frontend/src/lib/sightingsApi.js

const BASE = "/api";

function qsFromObj(obj) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) p.set(k, v.join(","));
    else p.set(k, String(v));
  }
  return p.toString();
}

/** Build query params for /api/sightings, ensuring buckets/is_night are mutually exclusive. */
function buildSightingsParams(q = {}) {
  const params = {
    start: q.start,
    end: q.end,
    order: q.order || "desc",
    limit: q.limit || 100,
    offset: q.offset || 0,
    plate: q.plate || "",
    exact: q.exact ? "true" : "",
    cameras: Array.isArray(q.cameras) ? q.cameras.join(",") : (q.cameras || ""),
  };

  // Only one of buckets / is_night.
  if (q.is_night === true || q.is_night === false) {
    params.is_night = q.is_night ? "1" : "0";
  } else if (Array.isArray(q.buckets) && q.buckets.length) {
    params.buckets = q.buckets.join(",");
  }

  return params;
}

/** Normalize API rows to { detection_id, ts, camera, plate } */
function normalizeRows(json) {
  const raw = Array.isArray(json) ? json : (json.rows || json.data || []);
  return raw.map((r) => ({
    detection_id: r.detection_id ?? r.id ?? null,
    ts: r.image_timestamp ?? r.time ?? r.ts ?? "",
    camera: String(r.camera ?? r.camera_id ?? ""),
    plate: r.plate ?? "",
  }));
}

/** Robust total: prefer header X-Total-Count, else meta.total, else JSON "total"/"count", else row count. */
function deriveTotal(res, json, rows) {
  const hdr = res.headers?.get?.("x-total-count");
  if (hdr && !Number.isNaN(Number(hdr))) return Number(hdr);
  if (json?.meta && Number.isFinite(json.meta.total)) return Number(json.meta.total);
  if (Number.isFinite(json?.total)) return Number(json.total);
  if (Number.isFinite(json?.count)) return Number(json.count);
  return rows.length;
}

/** Prefer server meta when available so UI reflects true paging/order. */
function deriveMeta(json, params) {
  const m = json?.meta || {};
  return {
    total: Number.isFinite(m.total) ? Number(m.total) : undefined,
    limit: Number.isFinite(m.limit) ? Number(m.limit) : Number(params.limit),
    offset: Number.isFinite(m.offset) ? Number(m.offset) : Number(params.offset),
    order: m.order || params.order,
    buckets: Array.isArray(m.buckets) ? m.buckets : undefined,
    is_night: m.is_night ?? undefined,
  };
}

// GET /api/sightings
export async function fetchSightings(q = {}) {
  const params = buildSightingsParams(q);
  const url = `${BASE}/sightings?${qsFromObj(params)}`;

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`Sightings HTTP ${res.status}`);

  let json = {};
  try { json = await res.json(); } catch { json = {}; }

  const rows = normalizeRows(json);
  const total = deriveTotal(res, json, rows);
  const meta = deriveMeta(json, params);

  return { rows, total, meta };
}

// GET /api/media/latest?detection_id=...
export async function fetchMediaByDetectionId(detectionId) {
  const url = `${BASE}/media/latest?detection_id=${encodeURIComponent(detectionId)}`;
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`Media HTTP ${res.status}`);
  return res.json();
}

/**
 * Simple CSV export for the **current page only** (fallback).
 * We keep this helper even though the page now uses the shared ExportMenu.
 */
export async function exportSightingsCSVCurrentPage(q = {}) {
  const { rows } = await fetchSightings(q);
  const header = "time,camera,plate\n";
  const body = rows.map((r) => `${r.ts},${r.camera},${r.plate || ""}`).join("\n");
  const blob = new Blob([header + body], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "sightings.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
